import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, ErrorNotice, FactList, LoadingState, Modal, Pill } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  LEAD_PANEL_QUERY_KEY,
  convertLead,
  eventDateLabel,
  previewConversion,
  type AccountChoice,
  type ConversionDone,
  type ConversionPlan,
  type PanelLead,
} from '@/lib/lead-panel';

const MATCH: AccountChoice = { mode: 'match' };
const NEW_ACCOUNT: AccountChoice = { mode: 'new' };

const MATCHED_BY: Readonly<Record<string, string>> = {
  'exact-email': 'matched by email',
  'email-domain': 'matched by company email domain',
  chosen: 'chosen by you',
};

export interface ConvertLeadDialogProps {
  /** The lead to convert; null keeps the dialog closed. */
  readonly lead: PanelLead | null;
  readonly onClose: () => void;
  /** After a conversion, so the panel refetches. */
  readonly onConverted: () => void;
}

function accountLine(plan: ConversionPlan): string {
  if (plan.account.mode === 'new') return `New account: ${plan.account.name}`;
  const moves = plan.account.newOwner ? `, moves to ${plan.account.newOwner.name}` : '';
  return `${plan.account.name} (${MATCHED_BY[plan.account.matchedBy] ?? plan.account.matchedBy}), owner ${plan.account.owner.name}${moves}`;
}

/**
 * Converts a lead the way the sales team does by hand. Shows what will happen
 * first (the same preview BEStie and ChatGPT give), lets the person pick a
 * different matching account or a new one, and converts only on confirm.
 */
export function ConvertLeadDialog({ lead, onClose, onConverted }: ConvertLeadDialogProps): React.ReactElement | null {
  const [account, setAccount] = useState<AccountChoice>(MATCH);
  const [done, setDone] = useState<ConversionDone | null>(null);
  const leadId = lead?.id ?? '';

  const preview = useQuery({
    queryKey: LEAD_PANEL_QUERY_KEY.preview(leadId, account),
    queryFn: () => previewConversion(leadId, account),
    enabled: lead !== null && done === null,
    retry: false,
  });
  const convert = useMutation({
    mutationFn: () => convertLead(leadId, account),
    onSuccess: (result) => {
      setDone(result);
      onConverted();
    },
  });

  function close(): void {
    setAccount(MATCH);
    setDone(null);
    convert.reset();
    onClose();
  }

  if (!lead) return null;
  const plan = preview.data;
  const title = `Convert ${lead.company || lead.contactName || 'lead'}`;

  if (done) {
    return (
      <Modal
        open
        title="Lead converted"
        onClose={close}
        className="max-w-lg"
        actions={
          <>
            <Button variant="secondary" onClick={close}>
              Close
            </Button>
            <Button onClick={() => window.open(done.opportunityUrl, '_blank', 'noopener')}>Open opportunity</Button>
          </>
        }
      >
        <p className="text-portal-small text-portal-ink">{done.plan.opportunity.name}</p>
        {done.warnings.length > 0 ? (
          <div className="mt-3">
            <ErrorNotice
              inline
              tone="warning"
              message={`Converted, but these still need doing in Salesforce: ${done.warnings.join(' ')}`}
            />
          </div>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={title}
      subtitle="Nothing changes in Salesforce until you convert."
      onClose={close}
      className="max-w-2xl"
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!plan || convert.isPending} onClick={() => convert.mutate()}>
            {convert.isPending ? 'Converting…' : 'Convert lead'}
          </Button>
        </>
      }
    >
      {preview.isError ? (
        <ErrorNotice inline message={errorMessage(preview.error)} />
      ) : !plan ? (
        <LoadingState label="Checking Salesforce for a matching account" />
      ) : (
        <div className="space-y-4">
          <FactList
            columns={2}
            facts={[
              { label: 'Account', value: accountLine(plan) },
              { label: 'Contact', value: plan.contactId ? 'Adds to the existing contact' : `New contact: ${plan.contactName}` },
              { label: 'Opportunity', value: plan.opportunity.name },
              { label: 'Salesperson', value: plan.opportunity.salesperson },
              { label: 'Close date (set by Salesforce)', value: eventDateLabel(plan.opportunity.closeDate) },
              { label: 'Minimum participants', value: plan.opportunity.minimumParticipants.toLocaleString('en-US') },
              { label: 'Lead source', value: plan.opportunity.leadSource },
              { label: 'Note on the opportunity', value: plan.note ? 'The inquiry text' : 'None (no description on the lead)' },
            ]}
          />
          {plan.notes.length > 0 ? (
            <ul className="space-y-1">
              {plan.notes.map((note) => (
                <li key={note}>
                  <Pill tone="info">{note}</Pill>
                </li>
              ))}
            </ul>
          ) : null}
          {plan.otherMatches.length > 0 || account.mode !== 'match' ? (
            <fieldset className="space-y-2 border-t border-portal-line pt-4">
              <legend className="text-portal-small font-bold text-portal-ink">Which account?</legend>
              {[
                { key: 'match', label: 'As suggested', choice: MATCH },
                ...plan.otherMatches.map((match) => ({
                  key: match.accountId,
                  label: `${match.accountName}${match.ownerName ? `, owner ${match.ownerName}` : ''}`,
                  choice: { mode: 'existing', accountId: match.accountId } as const,
                })),
                { key: 'new', label: 'Create a new account', choice: NEW_ACCOUNT },
              ].map((option) => {
                const checked =
                  option.choice.mode === account.mode &&
                  (option.choice.mode !== 'existing' || (account.mode === 'existing' && account.accountId === option.choice.accountId));
                return (
                  <label key={option.key} className="flex items-center gap-2 text-portal-small text-portal-ink">
                    <input type="radio" name="account" checked={checked} onChange={() => setAccount(option.choice)} />
                    {option.label}
                  </label>
                );
              })}
            </fieldset>
          ) : null}
          {convert.isError ? <ErrorNotice inline message={errorMessage(convert.error)} /> : null}
        </div>
      )}
    </Modal>
  );
}
